import { useState, useEffect, useRef } from 'react';
import type { KBTagTreeNode, KBTagSummary } from '../types/knowledgeBase';

interface TagSelectorProps {
  availableTags: KBTagTreeNode[];
  selectedTags: KBTagSummary[];
  onAddTag: (tagId: number) => void;
  onRemoveTag: (tagId: number) => void;
  disabled?: boolean;
}

export default function TagSelector({
  availableTags,
  selectedTags,
  onAddTag,
  onRemoveTag,
  disabled = false,
}: TagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Flatten the tag tree for searching
  const flattenTags = (nodes: KBTagTreeNode[], path: string = ''): Array<{ id: number; name: string; color: string | null; fullPath: string }> => {
    return nodes.flatMap((node) => {
      const fullPath = path ? `${path} > ${node.name}` : node.name;
      return [
        { id: node.id, name: node.name, color: node.color, fullPath },
        ...flattenTags(node.children || [], fullPath),
      ];
    });
  };

  const flatTags = flattenTags(availableTags);
  const selectedTagIds = new Set(selectedTags.map((t) => t.id));

  // Filter available tags (exclude already selected)
  const filteredTags = flatTags.filter(
    (tag) =>
      !selectedTagIds.has(tag.id) &&
      (tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tag.fullPath.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectTag = (tagId: number) => {
    onAddTag(tagId);
    setSearchQuery('');
    setIsOpen(false);
  };

  return (
    <div className="space-y-2">
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center px-2 py-1 rounded-full text-sm"
              style={{
                backgroundColor: tag.color ? `${tag.color}20` : '#f3f4f6',
                color: tag.color || '#4b5563',
              }}
            >
              {tag.color && (
                <span
                  className="w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: tag.color }}
                />
              )}
              <span title={tag.full_path}>{tag.name}</span>
              {!disabled && (
                <button
                  onClick={() => onRemoveTag(tag.id)}
                  className="ml-1.5 text-current opacity-60 hover:opacity-100"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Tag Input */}
      {!disabled && (
        <div className="relative" ref={dropdownRef}>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={selectedTags.length > 0 ? 'Add more tags...' : 'Add tags...'}
            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          />

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
              {filteredTags.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {searchQuery ? 'No matching tags' : 'No available tags'}
                </div>
              ) : (
                filteredTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleSelectTag(tag.id)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
                  >
                    {tag.color && (
                      <span
                        className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                    )}
                    <span className="flex-1">
                      <span className="font-medium">{tag.name}</span>
                      {tag.fullPath !== tag.name && (
                        <span className="ml-2 text-gray-400 text-xs">
                          {tag.fullPath}
                        </span>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {selectedTags.length === 0 && disabled && (
        <span className="text-sm text-gray-500">No tags assigned</span>
      )}
    </div>
  );
}
